<template>
  <div class="cms-form-group" :class="{ 'cms-form-group-full': fullWidth }">
    <label v-if="label">{{ label }}</label>
    <div class="cms-facts-input">
      <div v-for="(fact, index) in modelValue" :key="index" class="cms-fact-item">
        <cms-input
          :model-value="fact.question"
          :label="questionLabel"
          :placeholder="questionPlaceholder"
          :full-width="true"
          :disabled="disabled"
          @update:model-value="updateFact(index, 'question', $event)"
        />
        <cms-textarea
          :model-value="fact.answer"
          :label="answerLabel"
          :placeholder="answerPlaceholder"
          :rows="3"
          :full-width="true"
          :disabled="disabled"
          @update:model-value="updateFact(index, 'answer', $event)"
        />
        <cms-button
          variant="icon"
          icon="trash"
          :disabled="disabled"
          @click="removeFact(index)"
        />
      </div>
      <cms-button variant="secondary" icon="plus" :disabled="disabled" @click="addFact">
        {{ addButtonText }}
      </cms-button>
    </div>
    <span v-if="helpText" class="cms-form-help">{{ helpText }}</span>
  </div>
</template>

<script lang="ts" setup>
import CmsButton from './CmsButton.vue';
import CmsInput from './CmsInput.vue';
import CmsTextarea from './CmsTextarea.vue';

export interface TeamFact {
  question: string;
  answer: string;
}

interface Props {
  modelValue: TeamFact[];
  label?: string;
  questionLabel?: string;
  answerLabel?: string;
  questionPlaceholder?: string;
  answerPlaceholder?: string;
  addButtonText?: string;
  fullWidth?: boolean;
  helpText?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  questionLabel: 'Question',
  answerLabel: 'Answer',
  questionPlaceholder: '',
  answerPlaceholder: '',
  addButtonText: 'Add fact',
  fullWidth: false,
  disabled: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: TeamFact[]): void;
}>();

const updateFact = (index: number, field: keyof TeamFact, value: string) => {
  const next = [...props.modelValue];
  next[index] = { ...next[index], [field]: value };
  emit('update:modelValue', next);
};

const removeFact = (index: number) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
};

const addFact = () => {
  emit('update:modelValue', [...props.modelValue, { question: '', answer: '' }]);
};
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-facts-input {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cms-fact-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--cms-border-color, #e5e7eb);
  border-radius: 8px;
  background: #fafafa;
}
</style>
